/**
* RelytArt
*
* Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International
* Copyright (c) 2022 RelytArt
*
* License: https://docs.google.com/document/d/1UD7jhCYWRXSxhx4A5vMeGVTjujwLM39BmvjwA0eFFfk/edit?usp=sharing
*
*/

// errors
import InvalidApiRequestError from "errors/InvalidApiRequestError.js";

// models
import Model from "models/model/Model.js";

// orchestration
import ERRORS from "orchestration/firebase-library/errors/Errors.js";
import Firebase from "orchestration/firebase-library/Firebase.js";

// services
import StorageService from "orchestration/services/storage-service/StorageService.js";
import TransactionService from "orchestration/services/transaction-service/TransactionService.js";

// utils
import LoggerUtils from "utils/logger-utils/LoggerUtils.js";
import ValidationUtils from "utils/validation-utils/ValidationUtils.js";

// constants
const CONTENT_TYPE = String("application/json");

class PersistenceService {

  /**
  * @param {String} apiPath
  * @param {String} persistenceObjectId
  * @returns {Promise}
  * @throws {InvalidApiRequestError}
  */
  static async delete(apiPath, persistenceObjectId) {
    if(!ValidationUtils.string(apiPath)) {
      throw new InvalidApiRequestError(`Invalid apiPath: "${apiPath}".`);
    }

    if(!ValidationUtils.string(persistenceObjectId)) {
      throw new InvalidApiRequestError(`Invalid persistenceObjectId: "${persistenceObjectId}".`);
    }

    const transactionId = await TransactionService.createTransaction(apiPath, persistenceObjectId);
    return TransactionService.executeTransaction(
      apiPath,
      transactionId,
      { delete: true, id: persistenceObjectId }
    );
  }

  /**
  * @param {Object} peristenceObjectState
  * @returns {Model}
  */
  static async format(peristenceObjectState) {
    return new Model(peristenceObjectState);
  }

  /**
  * @param {Object} newState
  * @param {Object} oldState
  * @returns {Object} change-set
  */
   static generateChangeSet(newState = {}, oldState = {}) {
    const keys = Object.keys({ ...newState, ...oldState });
    const changeSet = {};
    for(const index in keys) {
      if(keys[index]) {
        const key = String(keys[index]);

        // ignore lastUpdated value (just for database reference)
        if(String(key) === String("lastUpdated")) continue;
        try {
          delete(newState[key].lastUpdated);
          delete(oldState[key].lastUpdated);
          if(newState[key] instanceof Array) newState[key].forEach((value) => delete(value.lastUpdated));
          if(oldState[key] instanceof Array) oldState[key].forEach((value) => delete(value.lastUpdated));
        }
        catch { /* ignore errors */ }

        // compare stringified values
        const newString = newState[key] ? JSON.stringify(newState[key]) : "undefined";
        const oldString = oldState[key] ? JSON.stringify(oldState[key]) : "undefined";
        if(String(newString) !== String(oldString)) {
          changeSet[key] = {};
          if(newState[key]) changeSet[key].new = newState[key];
          if(oldState[key]) changeSet[key].old = oldState[key];
        }
      }
    }
    return changeSet;
  }

  /**
  * @param {String} apiPath
  * @param {String} persistenceObjectId
  * @param {Boolean} ignoreMedia
  * @returns {Model}
  * @throws {Error}
  * @throws {InvalidApiRequestError}
  */
  static async get(apiPath, persistenceObjectId, ignoreMedia = false) {
    if(!ValidationUtils.string(apiPath)) {
      throw new InvalidApiRequestError(`Invalid apiPath: "${apiPath}".`);
    }

    if(!ValidationUtils.string(persistenceObjectId)) {
      throw new InvalidApiRequestError(`Invalid persistenceObjectId: "${persistenceObjectId}".`);
    }

    const peristenceObjectState = await Firebase.get(`${apiPath}/${persistenceObjectId}`);
    return this.format(peristenceObjectState, ignoreMedia);
  }

  /**
  * @param {String} apiPath
  * @param {Boolean} showHiddenItems
  * @param {Boolean} ignoreMedia
  * @returns {Model[]}
  * @throws {Error}
  */
  static async getAll(apiPath, showHiddenItems = false, ignoreMedia = false) {
    if(!ValidationUtils.string(apiPath)) {
      throw new InvalidApiRequestError(`Invalid apiPath: "${apiPath}".`);
    }
    // TODO move filtering, searching to service
    // TODO format pagination for 100+ items
    const persistenceObjects = await Firebase.get(apiPath);

    const formattedItems = [];
    await Promise.all(Object.entries(persistenceObjects).map(async ([key, persistenceState]) => {
      const id = String(key);
      const isHidden = Boolean(persistenceState.isHidden);
      const hideItem = (!showHiddenItems && isHidden) || String(id) === String("keys");
      if(!hideItem) {
        const formattedItem = await this.format(persistenceState, ignoreMedia);
        formattedItems.push(formattedItem);
      }
    }));
    return formattedItems;
  }

  /**
  * @param {String} apiPath
  * @param {Model} persistenceObject
  * @param {Function} onProgress
  * @returns {String} persistenceObjectId
  * @throws {InvalidApiRequestError}
  */
  static async save(apiPath, persistenceObject, onProgress) {
    if(!ValidationUtils.string(apiPath)) {
      throw new InvalidApiRequestError(`Invalid apiPath: "${apiPath}".`);
    }

    if(!ValidationUtils.exists(persistenceObject)) {
      throw new InvalidApiRequestError(`Invalid persistenceObject: "${JSON.stringify(persistenceObject)}".`);
    }

    try {
      let persistenceObjectRef = persistenceObject;

      // validate the Item
      // (throws error if invalid)
      persistenceObject.validate();

      // create a transaction
      const transactionId = await TransactionService.createTransaction(
        apiPath,
        persistenceObject.getId()
      );

      // pre-process
      if(this.preProcessTransaction instanceof Function) {
        persistenceObjectRef = await this.preProcessTransaction(
          persistenceObjectRef,
          transactionId,
          onProgress
        );
      }

      // upload state to transaction ingest
      await StorageService.upload(
        transactionId,
        String("payload.json"),
        new Blob(
          [JSON.stringify(persistenceObjectRef.getState())],
          { type: CONTENT_TYPE }
        ),
        { contentType: CONTENT_TYPE },
        onProgress
      );

      // execute transaction
      const persistenceObjectId = await TransactionService.executeTransaction(apiPath, transactionId);

      // post-process
      if(this.postProcessTransaction instanceof Function) {
        persistenceObjectRef = await this.postProcessTransaction(
          persistenceObjectId,
          transactionId,
          onProgress
        );
      }

      // return persistenceObjectId
      return persistenceObjectId;
    }
    catch(error) {
      LoggerUtils.error(error);
      // TODO proper error handling from TransactionService
      throw Error(String(ERRORS.databaseConnectionUnavailable));
    }
  }
}
export default PersistenceService;
